import { defineStore } from 'pinia';
import { type Ref, ref } from 'vue';

import { BANNER_INFO_URL } from '@/config';
import { env } from '@/env';
import { logger } from '@/utils/logger';

export type BannerSeverity = 'info' | 'success' | 'warn' | 'error';

export type OutageBanner = {
  id: string;
  title: string;
  message: string;
  severity: BannerSeverity;
  enabled: boolean;
  closed: boolean;
  url_regex?: string;
};

export type OutageBannerInput = Omit<OutageBanner, 'closed'> & { closed?: boolean };

export const useOutageBannersStore = defineStore('outage-banners', () => {
  const banners: Ref<OutageBanner[]> = ref([]);

  function closeBanner(id: string) {
    const index = banners.value.findIndex((b) => b.id === id);
    if (index !== -1) {
      banners.value[index] = {
        ...banners.value[index],
        closed: true,
      };
    }
  }

  function updateBanners(newBanners: OutageBannerInput[]) {
    const closedBannerIds = new Set(banners.value.filter((b) => b.closed).map((b) => b.id));

    banners.value = newBanners.map((newBanner) => ({
      ...newBanner,
      closed: closedBannerIds.has(newBanner.id) || false,
    }));
  }

  async function loadBannersFromCDN() {
    if (!env.outageBannersEnabled) {
      return;
    }

    try {
      const response = await fetch(BANNER_INFO_URL);

      if (response.status === 404) {
        updateBanners([]);
        return;
      }

      if (!response.ok) {
        logger.error(`Failed to load banners from CDN: ${response.status}`);
        return;
      }

      const data = await response.json();
      if (data.banners && Array.isArray(data.banners)) {
        updateBanners(data.banners);
      }
    } catch (error) {
      logger.warn('Could not load banners from CDN', {}, error as Error);
    }
  }

  void loadBannersFromCDN();

  return {
    banners,
    closeBanner,
    updateBanners,
    loadBannersFromCDN,
  };
});
